import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Typography,
    Input,
    Button,
    Form,
    Spin,
    App,
    Row,
    Col,
    Select,
    Radio
} from 'antd';
import { v4 as uuidv4 } from 'uuid';
import { cashierService } from '../../services/api';
import { usePageTitle } from '../../context/PageTitleContext';
import HorizontalFormItem from "../../components/HorizontalItems/FormItem";

const { Title } = Typography;

const CashierCreate = () => {
    const navigate = useNavigate();
    const { message } = App.useApp();
    const { setPageTitle, setRightContent } = usePageTitle();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({
        role: [],
        status: []
    });
    const [saving, setSaving] = useState(false);
    const [correlationKey, setCorrelationKey] = useState(null);
    const [idempotencyKey, setIdempotencyKey] = useState(null);
    const [previousBodyString, setPreviousBodyString] = useState('');

    useEffect(() => {
        fetchFormData();
        setPageTitle('Создание кассира');

        setRightContent(
            <Button
                type="primary"
                onClick={handleSave}
                loading={saving}
            >
                Создать
            </Button>
        );

        return () => {
            setPageTitle('');
            setRightContent(null);
        };
    }, [setPageTitle, setRightContent]);

    const fetchFormData = async () => {
        try {
            setLoading(true);
            // Получаем данные для формы
            const formDataResponse = await cashierService.getFormData();

            if (formDataResponse.status === 'success') {
                setFormData(formDataResponse.data);
                console.log('Получены данные для формы:', formDataResponse.data);
            } else {
                message.error('Не удалось загрузить данные для формы');
                navigate('/cashiers');
            }
        } catch (error) {
            console.error('Ошибка при загрузке данных:', error);
            message.error('Ошибка при загрузке данных для формы');
            navigate('/cashiers');
        } finally {
            setLoading(false);
        }
    };

    // Обработка сохранения данных кассира
    const handleSave = async () => {
        try {
            const values = await form.validateFields();

            // Получаем данные пользователя из localStorage
            const userData = JSON.parse(localStorage.getItem('user') || '{}');
            const username = userData.username || '';
            const firstName = userData.first_name || '';
            const lastName = userData.last_name || '';
            const fullName = `${firstName} ${lastName}`.trim();

            // Преобразование данных перед отправкой
            const formattedValues = {
                ...values,
                username: username,
                full_name: fullName
            };

            console.log('Сохраняемые данные:', formattedValues);

            // Генерируем или обновляем ключ идемпотентности
            const currentBodyString = JSON.stringify(formattedValues);
            let currentIdempotencyKey = idempotencyKey;
            if (!currentIdempotencyKey || previousBodyString !== currentBodyString) {
                currentIdempotencyKey = uuidv4();
                setIdempotencyKey(currentIdempotencyKey);
                setPreviousBodyString(currentBodyString);
            }

            // Всегда генерируем новый ключ корреляции
            const newCorrelationKey = uuidv4();
            setCorrelationKey(newCorrelationKey);

            setSaving(true);

            // Передаем оба ключа в API
            const response = await cashierService.createCashier(
                formattedValues,
                newCorrelationKey,
                currentIdempotencyKey
            );

            if (response.status === 'success') {
                message.success('Кассир успешно создан');
                // Переходим на страницу созданного кассира
                navigate(`/cashiers/${response.data.cashier.id}`);
            } else {
                message.error(response.message || 'Ошибка при создании кассира');
            }
        } catch (error) {
            console.error('Ошибка при создании:', error);
            message.error(error.message || 'Ошибка при создании кассира');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px 0' }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <Card>
            <Title level={3} style={{ marginTop: 0 }}>
                Создание нового кассира
            </Title>

            <Form
                form={form}
                layout="horizontal"
                initialValues={{
                    status: 'active'
                }}
            >
                <Row gutter={24}>
                    <Col span={12}>
                        <Card title="Личные данные" style={{ marginBottom: 24 }}>
                            <HorizontalFormItem
                                label="Фамилия"
                                name="l_name"
                                rules={[
                                    { required: true, message: 'Введите фамилию' },
                                    { max: 255, message: 'Максимальная длина - 255 символов' }
                                ]}
                            >
                                <Input placeholder="Введите фамилию" maxLength={255} />
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="Имя"
                                name="f_name"
                                rules={[
                                    { required: true, message: 'Введите имя' },
                                    { max: 255, message: 'Максимальная длина - 255 символов' }
                                ]}
                            >
                                <Input placeholder="Введите имя" maxLength={255} />
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="Отчество"
                                name="m_name"
                                rules={[
                                    { max: 255, message: 'Максимальная длина - 255 символов' }
                                ]}
                            >
                                <Input placeholder="Введите отчество" maxLength={255} />
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="Номер карты клиента"
                                name="customer_card_number"
                                rules={[
                                    { max: 16, message: 'Максимальная длина - 16 символов' }
                                ]}
                            >
                                <Input placeholder="Введите номер карты клиента" maxLength={16} />
                            </HorizontalFormItem>
                        </Card>
                    </Col>
                    <Col span={12}>
                        <Card title="Системная информация" style={{ marginBottom: 24 }}>
                            <HorizontalFormItem
                                label="Роль"
                                name="role"
                                rules={[
                                    { required: true, message: 'Выберите роль' }
                                ]}
                            >
                                <Select
                                    options={formData.role.map(roleItem => ({
                                        label: roleItem.label,
                                        value: roleItem.value
                                    }))}
                                    placeholder="Выберите роль"
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                />
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="Статус"
                                name="status"
                                rules={[
                                    { required: true, message: 'Выберите статус' }
                                ]}
                            >
                                <Radio.Group>
                                    {formData.status.map(statusItem => (
                                        <Radio.Button
                                            key={statusItem.value}
                                            value={statusItem.value}
                                        >
                                            {statusItem.label}
                                        </Radio.Button>
                                    ))}
                                </Radio.Group>
                            </HorizontalFormItem>
                        </Card>
                    </Col>
                </Row>
            </Form>
        </Card>
    );
}

export default CashierCreate;